import { useEffect, useRef, useCallback } from "react";
import type { DealClientListData } from "~/types/deal";
import { AnalyticsEventBuilder } from "~/lib/analytics";
import { useUserStore } from "~/lib/stores/user";
import { devLog } from "~/lib/dev-log";

interface ImpressionTrackingOptions {
  deal: DealClientListData;
  channel?: string;
  position?: number;
  /** Time in milliseconds the deal must be visible before tracking (default: 500ms) */
  visibilityDuration?: number;
  /** Intersection ratio threshold (default: 0.5 for 50%) */
  threshold?: number;
}

/**
 * Hook to track deal impression using Intersection Observer
 * Sends analytics event when:
 * 1. Over half of the card is visible (threshold > 0.5)
 * 2. Deal is shown for over 0.5 seconds
 */
export function useImpressionTracking({
  deal,
  channel,
  position = 0,
  visibilityDuration = 500,
  threshold = 0.5,
}: ImpressionTrackingOptions) {
  const elementRef = useRef<HTMLDivElement>(null);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedRef = useRef(false);
  const { user } = useUserStore();

  const trackImpression = useCallback(() => {
    if (!user) {
      devLog("⚠️ Cannot track impression: user not available");
      return;
    }

    devLog(`📊 Tracking impression for deal ${deal._id} at position ${position}`);

    // Build context attributes (channel is already a JSON string)
    const contextAttrs = AnalyticsEventBuilder.createContextAttrs(channel || "", position);

    // Build user attributes
    const userAttrs = AnalyticsEventBuilder.createUserAttrs(user);

    // Build item attributes
    const itemAttrs = AnalyticsEventBuilder.createItemAttrs({
      currency: user.currency || "USD",
      price_dash: deal.price || 0,
    });

    // Send impression event
    AnalyticsEventBuilder.sendImpressionEvent(
      user._id,
      deal._id,
      deal._productId,
      deal.reqId || "",
      [],
      contextAttrs,
      userAttrs,
      itemAttrs
    );
  }, [user, deal, channel, position]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !user || hasTrackedRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            // Card is now visible with sufficient ratio
            devLog(
              `👁️ Deal ${deal._id} became visible (${(entry.intersectionRatio * 100).toFixed(0)}%), starting timer...`
            );

            // Start timer for visibility duration
            visibilityTimerRef.current = setTimeout(() => {
              if (!hasTrackedRef.current) {
                hasTrackedRef.current = true;
                trackImpression();
              }
            }, visibilityDuration);
          } else {
            // Card is no longer visible or below threshold, clear timer
            if (visibilityTimerRef.current) {
              devLog(`👁️ Deal ${deal._id} no longer visible, clearing timer`);
              clearTimeout(visibilityTimerRef.current);
              visibilityTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold: [threshold],
        rootMargin: "0px",
      }
    );

    observer.observe(element);

    return () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
      observer.disconnect();
    };
  }, [deal._id, user, threshold, visibilityDuration, trackImpression]);

  return elementRef;
}
